/**
 * i18n setup: vue-i18n instance plus the app-wide locale primitive that
 * `config/date.ts`/`config/money.ts` build their Intl locale defaults from,
 * so switching the UI language also switches number/date formatting.
 *
 * Persisted to localStorage; defaults to Russian for a first-time visitor
 * (the business's primary language), not browser auto-detect.
 */
import { computed, watch } from 'vue'
import { createI18n } from 'vue-i18n'
import en from './locales/en.json'
import ru from './locales/ru.json'

export type AppLocale = 'en' | 'ru'

const STORAGE_KEY = 'plyas-locale'

function isAppLocale(value: string | null): value is AppLocale {
  return value === 'en' || value === 'ru'
}

function initialLocale(): AppLocale {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
  return isAppLocale(stored) ? stored : 'ru'
}

/**
 * Russian plural form selection (CLDR one/few/many) for 3-form messages like
 * `"{count} платёж | {count} платежа | {count} платежей"`. vue-i18n's
 * built-in default rule only handles English-style singular/plural — a
 * bare `choice % 10` check would mis-pluralize the 11–14 range (which
 * always takes the "many" form, not "one"/"few", despite ending in 1–4).
 */
function ruPluralRule(choice: number): number {
  const teen = choice > 10 && choice < 20
  const endsInOne = choice % 10 === 1
  if (!teen && endsInOne) return 0 // one — "1 платёж", "21 платёж"
  const endsIn234 = choice % 10 >= 2 && choice % 10 <= 4
  if (!teen && endsIn234) return 1 // few — "3 платежа", "24 платежа"
  return 2 // many — "5 платежей", "11 платежей", "0 платежей"
}

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale(),
  fallbackLocale: 'en',
  messages: { en, ru },
  pluralRules: { ru: ruPluralRule },
})

/** Writable ref for the current UI language; assigning to `.value` switches translated text app-wide. */
export const appLocale = i18n.global.locale

if (typeof localStorage !== 'undefined') {
  watch(appLocale, (value) => {
    localStorage.setItem(STORAGE_KEY, value)
  })
}

/** Maps the UI language to a BCP 47 Intl locale tag (`en` → `en-US`, `ru` → `ru-RU`). */
function intlLocaleFor(locale: AppLocale): string {
  return locale === 'ru' ? 'ru-RU' : 'en-US'
}

/** Reactive Intl locale tag tracking `appLocale` — the single source `config/date.ts`/`config/money.ts` read from. */
export const intlLocale = computed(() => intlLocaleFor(appLocale.value as AppLocale))
